// CVSS 3.1: the base score, the temporal score, and reading or writing a vector string. No page code here.
const W = {
  AV: { N: 0.85, A: 0.62, L: 0.55, P: 0.2 },
  AC: { L: 0.77, H: 0.44 },
  UI: { N: 0.85, R: 0.62 },
  CIA: { H: 0.56, L: 0.22, N: 0 },
  PR_U: { N: 0.85, L: 0.62, H: 0.27 },
  PR_C: { N: 0.85, L: 0.68, H: 0.5 },
  E: { X: 1, H: 1, F: 0.97, P: 0.94, U: 0.91 },
  RL: { X: 1, U: 1, W: 0.97, T: 0.96, O: 0.95 },
  RC: { X: 1, C: 1, R: 0.96, U: 0.92 }
};

export const BASE_KEYS = ['AV', 'AC', 'PR', 'UI', 'S', 'C', 'I', 'A'];
export const TEMPORAL_KEYS = ['E', 'RL', 'RC'];
export const DEFAULT_BASE = { AV: 'N', AC: 'L', PR: 'N', UI: 'N', S: 'U', C: 'N', I: 'N', A: 'N' };
export const DEFAULT_TEMPORAL = { E: 'X', RL: 'X', RC: 'X' };

// The official rounding, done on whole numbers so 4.000000001 does not become 4.1.
export const roundUp = (x) => {
  const n = Math.round(x * 100000);
  return n % 10000 === 0 ? n / 100000 : (Math.floor(n / 10000) + 1) / 10;
};

export const rating = (score) => (score === 0 ? 'None' : score < 4 ? 'Low' : score < 7 ? 'Medium' : score < 9 ? 'High' : 'Critical');

export function baseScore(v) {
  const changed = v.S === 'C';
  const iss = 1 - (1 - W.CIA[v.C]) * (1 - W.CIA[v.I]) * (1 - W.CIA[v.A]);
  const impact = changed ? 7.52 * (iss - 0.029) - 3.25 * (iss - 0.02) ** 15 : 6.42 * iss;
  const exploitability = 8.22 * W.AV[v.AV] * W.AC[v.AC] * (changed ? W.PR_C : W.PR_U)[v.PR] * W.UI[v.UI];
  if (impact <= 0) return { score: 0, impact, exploitability };
  const raw = changed ? 1.08 * (impact + exploitability) : impact + exploitability;
  return { score: roundUp(Math.min(raw, 10)), impact, exploitability };
}

export const temporalScore = (base, t) => roundUp(base * W.E[t.E] * W.RL[t.RL] * W.RC[t.RC]);

export function calculate(base, temporal = DEFAULT_TEMPORAL) {
  const b = baseScore(base);
  const tScore = temporalScore(b.score, temporal);
  return { base: b.score, temporal: tScore, rating: rating(b.score), temporalRating: rating(tScore), impact: Math.round(b.impact * 10) / 10, exploitability: Math.round(b.exploitability * 10) / 10 };
}

export function toVector(base, temporal = DEFAULT_TEMPORAL) {
  const main = `CVSS:3.1/${BASE_KEYS.map((k) => `${k}:${base[k]}`).join('/')}`;
  const extra = TEMPORAL_KEYS.filter((k) => temporal[k] !== 'X').map((k) => `${k}:${temporal[k]}`);
  return extra.length ? `${main}/${extra.join('/')}` : main;
}

const ALLOWED = {
  AV: 'NALP', AC: 'LH', PR: 'NLH', UI: 'NR', S: 'UC', C: 'NLH', I: 'NLH', A: 'NLH', E: 'XHFPU', RL: 'XUWTO', RC: 'XCRU'
};

// Reads CVSS:3.0 / 3.1 vectors. Returns { base, temporal } or { error } saying what is wrong.
export function parseVector(text) {
  const parts = String(text || '').trim().split('/').filter(Boolean);
  if (!parts.length) return { error: 'Paste a vector, for example CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H.' };
  const head = parts[0].toUpperCase();
  if (head.startsWith('CVSS:4')) return { error: 'This calculator handles CVSS 3.x. Version 4.0 vectors are not supported yet.' };
  if (!/^CVSS:3\.[01]$/.test(head)) return { error: 'The vector must start with CVSS:3.1/.' };
  const found = {};
  for (const part of parts.slice(1)) {
    const [k, val] = part.toUpperCase().split(':');
    if (!ALLOWED[k] || !val || val.length !== 1 || !ALLOWED[k].includes(val)) return { error: `"${part}" is not a valid metric.` };
    if (found[k]) return { error: `${k} appears twice.` };
    found[k] = val;
  }
  const missing = BASE_KEYS.filter((k) => !found[k]);
  if (missing.length) return { error: `Missing: ${missing.join(', ')}.` };
  return { base: Object.fromEntries(BASE_KEYS.map((k) => [k, found[k]])), temporal: Object.fromEntries(TEMPORAL_KEYS.map((k) => [k, found[k] || 'X'])) };
}

// The questions, in plain words. The metric letters stay in the vector, the people see these.
export const QUESTIONS = [
  { group: 'About the attack', key: 'AV', q: 'Where can the attacker be?', hint: 'Attack vector', options: [['N', 'Anywhere on the internet'], ['A', 'On the same network'], ['L', 'On the machine itself'], ['P', 'Holding the device']] },
  { group: 'About the attack', key: 'AC', q: 'Is it hard to pull off?', hint: 'Attack complexity', options: [['L', 'Easy'], ['H', 'Needs special conditions']] },
  { group: 'About the attack', key: 'PR', q: 'Do they need an account?', hint: 'Privileges required', options: [['N', 'No account'], ['L', 'A normal account'], ['H', 'An admin account']] },
  { group: 'About the attack', key: 'UI', q: 'Must a user click something?', hint: 'User interaction', options: [['N', 'No'], ['R', 'Yes']] },
  { group: 'About the damage', key: 'S', q: 'Can it reach beyond the vulnerable part?', hint: 'Scope', options: [['U', 'No, it stays inside'], ['C', 'Yes, it spreads']] },
  { group: 'About the damage', key: 'C', q: 'Can secrets be read?', hint: 'Confidentiality', options: [['N', 'No'], ['L', 'A little'], ['H', 'Everything']] },
  { group: 'About the damage', key: 'I', q: 'Can data be changed?', hint: 'Integrity', options: [['N', 'No'], ['L', 'A little'], ['H', 'Everything']] },
  { group: 'About the damage', key: 'A', q: 'Can the service be stopped?', hint: 'Availability', options: [['N', 'No'], ['L', 'A little'], ['H', 'Fully']] }
];

// One or two sentences a non-expert can read.
export function explain(v) {
  const where = { N: 'over the internet', A: 'from the same network', L: 'on the machine itself', P: 'with physical access' }[v.AV];
  const effort = v.AC === 'L' ? 'Easy to attack' : 'Hard to attack';
  const login = { N: 'no login needed', L: 'needs a normal account', H: 'needs an admin account' }[v.PR];
  const click = v.UI === 'R' ? ', and a user must click something' : '';
  const hits = [v.C !== 'N' && 'secrets', v.I !== 'N' && 'data', v.A !== 'N' && 'availability'].filter(Boolean);
  const worst = [v.C, v.I, v.A].includes('H') ? 'badly ' : '';
  const damage = hits.length ? `It ${worst}affects ${hits.length > 1 ? `${hits.slice(0, -1).join(', ')} and ${hits[hits.length - 1]}` : hits[0]}${v.S === 'C' ? ' and can spread beyond the vulnerable part' : ''}.` : 'It does no damage.';
  return `${effort} ${where}, ${login}${click}. ${damage}`;
}
